import {
	lazy,
	Suspense,
	useId,
	useState,
	type ComponentType,
	type ReactNode,
} from 'react';
import { Link } from '@tanstack/react-router';

import { cn } from '@/lib/utils';

import {
	formatCount,
	formatDay,
	formatDayOfMonth,
	formatDays,
	formatHour,
	formatHourRange,
	formatPercent,
	formatReadingTime,
	formatWeekday,
	getDailyStats,
	getHourlyStats,
	getReadingStreak,
	getRecentIssues,
	getTodayStats,
	getWeekStats,
	getYearStats,
	useReaderStats,
	type StatsBucket,
	type ReadingTotals,
} from '@entities/reader-stats';
import { useLanguage, useT, type MessageKey } from '@shared/i18n';
import { readerStatsPreference } from '@shared/lib/reader-preferences';

import type { SettingControlProps } from './setting-controls';
import styles from './settings-dialog.module.css';

const StatsChart = lazy(() => import('./stats-chart'));

/**
 * Settings → Reading stats: the reader's reading across every issue, the
 * same whichever screen the dialog is opened from. The figures for one
 * issue are in the reader's own Stats dialog (`widgets/issue-reader`). All
 * of it comes from `@entities/reader-stats`, which lives on the device and
 * is never sent.
 */

/** One figure: a small label over a large value. */
function Stat({ label, value }: { label: string; value: ReactNode }) {
	return (
		<div className={styles['stat']}>
			<dt>{label}</dt>
			<dd>{value}</dd>
		</div>
	);
}

/** Shown in place of figures when the reader has switched them off. */
function useStatsOff(): string | null {
	const t = useT();
	const [setting] = readerStatsPreference.use();
	return setting === 'off' ? t('stats.off') : null;
}

// ---- Settings → Reading stats ---------------------------------------------

function Period({ title, totals }: { title: string; totals: ReadingTotals }) {
	const t = useT();
	const language = useLanguage();
	return (
		<section className={styles['stats-period']}>
			<h5>{title}</h5>
			<dl className={styles['stats']}>
				<Stat
					label={t('stats.timeRead')}
					value={formatReadingTime(totals.readMs, language)}
				/>
				{totals.listenedMs > 0 ? (
					<Stat
						label={t('stats.timeListened')}
						value={formatReadingTime(totals.listenedMs, language)}
					/>
				) : null}
				<Stat
					label={t('stats.articlesFinished')}
					value={formatCount(totals.articlesCompleted, language)}
				/>
				<Stat
					label={t('stats.issuesFinished')}
					value={formatCount(totals.issuesCompleted, language)}
				/>
			</dl>
		</section>
	);
}

function StatsOverviewPanel(_props: SettingControlProps) {
	const t = useT();
	const language = useLanguage();
	const off = useStatsOff();
	const data = useReaderStats(async () => {
		const [today, week, year, streak] = await Promise.all([
			getTodayStats(),
			getWeekStats(),
			getYearStats(),
			getReadingStreak(),
		]);
		return { today, week, year, streak };
	}, []);

	if (!data) {
		return null;
	}
	return (
		<div className={styles['panel']}>
			{off ? <p className={styles['value']}>{off}</p> : null}
			<dl className={styles['stats']}>
				<Stat
					label={t('stats.streak')}
					value={formatDays(data.streak, language)}
				/>
			</dl>
			<Period title={t('stats.today')} totals={data.today} />
			<Period title={t('stats.week')} totals={data.week} />
			<Period title={t('stats.year')} totals={data.year} />
		</div>
	);
}

// ---- Activity: charts by hour or by day ------------------------------------

/** What the charts span: today by the hour, or the last days by the day. */
type Frame = 'today' | 'week' | 'month';
const FRAMES: ReadonlyArray<{ value: Frame; label: MessageKey }> = [
	{ value: 'today', label: 'stats.today' },
	{ value: 'week', label: 'stats.week' },
	{ value: 'month', label: 'stats.month' },
];

const loadFrame = (frame: Frame): Promise<Array<StatsBucket>> =>
	frame === 'today' ? getHourlyStats() : getDailyStats(frame === 'week' ? 7 : 30);

/**
 * The text under a column. Every day of a week has room for its name; with
 * 24 or 30 columns only some are named (every sixth hour, every seventh day
 * counting back from today) and the rest are told apart by their position.
 */
function axisLabel(
	frame: Frame,
	bucket: StatsBucket,
	index: number,
	count: number,
	language: string,
): string {
	if (frame === 'week') {
		return formatWeekday(bucket.timestamp, language);
	}
	if (frame === 'today') {
		return index % 6 === 0 ? formatHour(bucket.timestamp, language) : '';
	}
	return (count - 1 - index) % 7 === 0
		? formatDayOfMonth(bucket.timestamp, language)
		: '';
}

/** A column named in full: a heading, and a button's accessible name. */
const fullName = (frame: Frame, bucket: StatsBucket, language: string): string =>
	frame === 'today'
		? formatHourRange(bucket.timestamp, language)
		: formatDay(bucket.timestamp, language);

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
/** Tops of a time scale that read as one short figure: `30 min`, `2 hr`. */
const TIME_TOPS = [
	10 * SECOND,
	30 * SECOND,
	MINUTE,
	2 * MINUTE,
	5 * MINUTE,
	10 * MINUTE,
	15 * MINUTE,
	20 * MINUTE,
	30 * MINUTE,
	45 * MINUTE,
	HOUR,
	2 * HOUR,
	3 * HOUR,
	4 * HOUR,
	6 * HOUR,
	8 * HOUR,
	12 * HOUR,
	24 * HOUR,
];

/** The round time at or above `ms`, for the top of a chart's scale. */
export function roundTimeUp(ms: number): number {
	return (
		TIME_TOPS.find((candidate) => candidate >= ms) ?? Math.ceil(ms / (24 * HOUR)) * 24 * HOUR
	);
}

/** The round count at or above `value`: 2, 4, 10, 20, 50, 100... (even, so half is whole). */
export function roundCountUp(value: number): number {
	for (let scale = 1; ; scale *= 10) {
		for (const step of [2, 4, 10]) {
			if (step * scale >= value) {
				return step * scale;
			}
		}
	}
}

function BucketChart({
	title,
	buckets,
	valueOf,
	format,
	kind,
	labelOf,
	ceil,
	selected,
	onSelect,
}: {
	title: string;
	buckets: Array<StatsBucket>;
	kind: 'bar' | 'line';
	/** A column named in full, for the tooltip. */
	labelOf: (index: number) => string;
	/** The round figure at or above a value, for the top of the scale. */
	ceil: (value: number) => number;
	valueOf: (bucket: StatsBucket) => number;
	format: (value: number) => string;
	selected: string;
	onSelect: (key: string) => void;
}) {
	const t = useT();
	const points = buckets.map((bucket) => ({
		key: bucket.key,
		value: valueOf(bucket),
	}));
	return (
		<section className={styles['stats-chart-block']}>
			<h5>{title}</h5>
			{/* The same height before the chart has loaded, so nothing jumps.
			    Drawn also with nothing to show: an empty grid (and, over
			    thirty days, a row of muted dots) says "nothing read", where a
			    blank box looks broken. */}
			<div className={styles['stats-chart-frame']}>
				<Suspense fallback={null}>
					<StatsChart
						days={points}
						selected={selected}
						onSelect={onSelect}
						format={format}
						kind={kind}
						name={title}
						labelOf={labelOf}
						ceil={ceil}
						averageLabel={t('stats.average')}
					/>
				</Suspense>
			</div>
		</section>
	);
}

function StatsDailyPanel({ labelId }: SettingControlProps) {
	const t = useT();
	const language = useLanguage();
	const name = useId();
	const [frame, setFrame] = useState<Frame>('week');
	// Loaded together with the frame they belong to. Until a newly chosen
	// frame has loaded, the one before it stays on screen, whole (columns,
	// axis and figures): nothing is taken away and put back, so nothing
	// flashes or changes height.
	const data = useReaderStats(async () => {
		const [buckets, month] = await Promise.all([
			loadFrame(frame),
			getDailyStats(30),
		]);
		// Listening has a chart of its own only for a reader who listens:
		// most issues have no audio. Decided over the thirty days whatever
		// the frame, so changing frame never adds or removes a chart.
		const listens = month.some((day) => day.listenedMs > 0);
		return { frame, buckets, listens };
	}, [frame]);
	const [chosen, setChosen] = useState<string>();

	const shown = data?.frame ?? frame;
	const buckets = data?.buckets;
	// Now until the reader chooses: this hour of today, or today of the days.
	// Also when what was chosen is not in the frame (another frame's column,
	// or a day that has scrolled out past midnight).
	const fallback =
		shown === 'today' ? buckets?.[new Date().getHours()] : buckets?.[buckets.length - 1];
	const selected = buckets?.find((bucket) => bucket.key === chosen) ?? fallback;
	// Thirty days are a line with a dot a day, not columns.
	const kind = shown === 'month' ? 'line' : 'bar';
	const labelOf = (index: number) =>
		buckets?.[index] ? fullName(shown, buckets[index], language) : '';

	return (
		<div className={styles['panel']}>
			<div
				role="radiogroup"
				aria-labelledby={labelId}
				className={cn(styles['segmented'], styles['stats-frames'])}
			>
				{FRAMES.map((option) => (
					<label key={option.value}>
						<input
							type="radio"
							name={name}
							value={option.value}
							checked={frame === option.value}
							onChange={() => setFrame(option.value)}
						/>
						<span>{t(option.label)}</span>
					</label>
				))}
			</div>
			{buckets && selected ? (
				// Keyed by the frame on screen, so a change of frame eases the
				// new charts in (see `.stats-frame`).
				<div key={shown} className={styles['stats-frame']}>
					<BucketChart
						title={t('stats.timeRead')}
						buckets={buckets}
						valueOf={(bucket) => bucket.readMs}
						format={(value) => formatReadingTime(value, language)}
						ceil={roundTimeUp}
						kind={kind}
						labelOf={labelOf}
						selected={selected.key}
						onSelect={setChosen}
					/>
					{data?.listens ? (
						<BucketChart
							title={t('stats.timeListened')}
							buckets={buckets}
							valueOf={(bucket) => bucket.listenedMs}
							format={(value) => formatReadingTime(value, language)}
							ceil={roundTimeUp}
							kind={kind}
							labelOf={labelOf}
							selected={selected.key}
							onSelect={setChosen}
						/>
					) : null}
					<BucketChart
						title={t('stats.articlesFinished')}
						buckets={buckets}
						valueOf={(bucket) => bucket.articlesCompleted}
						format={(value) => formatCount(value, language)}
						ceil={roundCountUp}
						kind={kind}
						labelOf={labelOf}
						selected={selected.key}
						onSelect={setChosen}
					/>
					{/* The charts' axis, and how a column is chosen without a pointer. */}
					<div
						className={styles['stats-days']}
						data-dense={buckets.length > 7 ? '' : undefined}
						style={{
							gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))`,
						}}
					>
						{buckets.map((bucket, index) => (
							<button
								key={bucket.key}
								type="button"
								className={styles['stats-day']}
								aria-pressed={bucket.key === selected.key}
								aria-label={fullName(shown, bucket, language)}
								onClick={() => setChosen(bucket.key)}
							>
								{axisLabel(shown, bucket, index, buckets.length, language)}
							</button>
						))}
					</div>
					<Period title={fullName(shown, selected, language)} totals={selected} />
				</div>
			) : (
				// The same room before the first figures arrive.
				<div className={styles['stats-frame']} />
			)}
		</div>
	);
}

function StatsRecentPanel(_props: SettingControlProps) {
	const t = useT();
	const language = useLanguage();
	const issues = useReaderStats(() => getRecentIssues(8), []);

	if (!issues) {
		return null;
	}
	if (!issues.length) {
		return <p className={styles['value']}>{t('stats.empty')}</p>;
	}
	return (
		<ul className={styles['list']}>
			{issues.map((issue) => (
				<li key={issue.issueId}>
					{/* Back to the issue. The link carries no `?settings=`, so
					    following it closes this dialog. */}
					<Link
						to="/issues/$id"
						params={{ id: String(issue.issueId) }}
						className={cn(styles['list-text'], styles['list-link'])}
					>
						<span dir="auto">{issue.title ?? `#${issue.issueId}`}</span>
						<span>
							{formatReadingTime(issue.readMs, language)}
							{issue.unitsTotal
								? ` · ${formatPercent(issue.unitsSeen / issue.unitsTotal, language)}`
								: ''}
							{issue.completedAt ? ` · ${t('stats.finished')}` : ''}
						</span>
					</Link>
				</li>
			))}
		</ul>
	);
}

export const STATS_PANELS: Record<string, ComponentType<SettingControlProps>> = {
	'stats-overview': StatsOverviewPanel,
	'stats-daily': StatsDailyPanel,
	'stats-recent': StatsRecentPanel,
};
